import React from "react";
import { ArrowRight, ShieldCheck, Clock, Wrench, BadgeCheck } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1524514587686-e2909d726e9b?auto=format&fit=crop&w=2400&q=85";
const HERO_COMPRESSOR =
  "https://images.unsplash.com/photo-1738918922725-d70c666ddccb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwzfHxpbmR1c3RyaWFsJTIwYWlyJTIwY29tcHJlc3NvciUyMGZhY3RvcnklMjBtYWNoaW5lcnl8ZW58MHx8fHwxNzc4MTI1MTQxfDA&ixlib=rb-4.1.0&q=85";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const Stat = ({ value, label, testId }) => (
  <div data-testid={testId} className="border-l border-slate-700 pl-4">
    <div className="font-heading text-3xl md:text-4xl font-black text-white">{value}</div>
    <div className="text-[11px] font-bold tracking-[0.2em] uppercase text-slate-300 mt-1">
      {label}
    </div>
  </div>
);

export const Hero = () => {
  return (
    <section data-testid="hero-section" className="relative overflow-hidden bg-slate-900">
      <div
        className="absolute inset-0 bg-cover bg-center opacity-90"
        style={{ backgroundImage: `url(${HERO_IMG})` }}
        aria-hidden
      />
      {/* Left-side dark gradient so headline stays readable on the compressor-room image */}
      <div className="absolute inset-0 bg-gradient-to-r from-slate-950/95 via-slate-900/70 to-slate-900/10" aria-hidden />
      <div className="absolute inset-0 bg-gradient-to-b from-slate-900/40 via-transparent to-slate-900" aria-hidden />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_40%,rgba(37,99,235,0.18),transparent_55%)]" aria-hidden />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-36 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-8 stagger-item">
          <div className="inline-flex items-center gap-2 bg-blue-600 text-white px-3 py-1.5 mb-8">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-white"></span>
            </span>
            <span className="text-[11px] font-bold tracking-[0.2em] uppercase">After-Hours Service Available</span>
          </div>

          <h1
            data-testid="hero-headline"
            className="font-heading text-5xl md:text-7xl font-black tracking-tighter uppercase text-white leading-[0.95]"
          >
            Compressed Air,
            <span className="block text-blue-400">Uncompromised Service.</span>
          </h1>

          <p className="mt-6 max-w-2xl text-base md:text-lg text-slate-300 leading-relaxed">
            Pneupro keeps your operations breathing. Repairs, planned maintenance, and after-hours
            response for industrial air compressor systems — delivered by certified millwrights,
            priced to make sense.
          </p>

          <div className="mt-10 flex flex-wrap gap-4">
            <button
              data-testid="hero-request-button"
              onClick={() => scrollTo("request")}
              className="cta-glow bg-blue-600 text-white hover:bg-blue-500 font-bold tracking-wide uppercase px-8 py-4 inline-flex items-center gap-2 transition-all hover:-translate-y-0.5"
            >
              Request Service <ArrowRight className="h-4 w-4" />
            </button>
            <button
              data-testid="hero-services-button"
              onClick={() => scrollTo("services")}
              className="border-2 border-white/30 text-white hover:bg-white hover:text-slate-900 font-bold tracking-wide uppercase px-6 py-4 inline-flex items-center gap-2 transition-all hover:-translate-y-0.5"
            >
              View Services
            </button>
          </div>
        </div>

        <div className="lg:col-span-4 self-end stagger-item" style={{ animationDelay: "0.15s" }}>
          <div className="grid grid-cols-2 gap-6 mb-6">
            <Stat testId="hero-stat-response" value="<2h" label="Avg Response" />
            <Stat testId="hero-stat-uptime" value="99.6%" label="Uptime Promise" />
          </div>
          <div
            data-testid="hero-millwrights-badge"
            className="bg-blue-600 text-white p-4 mb-6 flex items-center gap-3"
          >
            <BadgeCheck className="h-6 w-6 shrink-0" strokeWidth={2.25} />
            <div className="leading-tight">
              <div className="font-heading text-base font-black uppercase tracking-tight">
                Certified Millwrights
              </div>
              <div className="text-[10px] font-bold tracking-widest uppercase opacity-90">
                Trade-qualified service team
              </div>
            </div>
          </div>
          <div className="bg-white/5 border border-white/10 backdrop-blur-md p-5">
            <div className="grid grid-cols-3 gap-3 text-white">
              <div className="text-center">
                <Wrench className="h-5 w-5 mx-auto text-blue-400" />
                <div className="text-[10px] mt-2 font-bold tracking-widest uppercase">Repairs</div>
              </div>
              <div className="text-center">
                <ShieldCheck className="h-5 w-5 mx-auto text-blue-400" />
                <div className="text-[10px] mt-2 font-bold tracking-widest uppercase">Maintenance</div>
              </div>
              <div className="text-center">
                <Clock className="h-5 w-5 mx-auto text-blue-400" />
                <div className="text-[10px] mt-2 font-bold tracking-widest uppercase">After-Hours</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
